'use client'

import { useMemo, useState } from 'react'
import { usePathname } from 'next/navigation'
import { motion } from 'framer-motion'
import { ArrowRight, ChevronRight, Search, Tag } from 'lucide-react'
import { formatImageURL } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'
import BrandCard from './BrandCard'

export default function BrandList({ brands, vendor }: { brands: any, vendor?: any }) {
  const pathname = usePathname()
  const list = brands || []
  const vendorName = vendor?.name || 'Дэлгүүр'
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    if (!query.trim()) return list
    const q = query.trim().toLowerCase()
    return list.filter((b: any) => String(b?.name || '').toLowerCase().includes(q))
  }, [list, query])

  const spotlight = list.find((b: any) => b?.thumbnail) || null
  const rest = spotlight ? filtered.filter((b: any) => b.id !== spotlight.id) : filtered

  return (
    <div>
      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pt-10 pb-10">
        <div className="hidden md:flex items-center gap-1.5 text-[12px] mb-8" style={{ color: 'rgba(255,255,255,0.55)' }}>
          <a href={navigateUrl('/', pathname)} className="hover:text-white transition-colors">Нүүр</a>
          <ChevronRight className="h-3 w-3" style={{ opacity: 0.5 }} />
          <span className="text-white font-medium">Брэнд</span>
        </div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: 'easeOut' }}>
          <div className="mt-3 flex items-end justify-between gap-8 flex-wrap">
            <h1
              className="font-semibold tracking-[-0.025em] leading-[1.02] sm:leading-[0.95] text-white max-w-3xl"
              style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(28px, 7vw, 64px)' }}
            >
              Дэлхийн сонгомол<br />брэндүүд нэг дороос.
            </h1>
            <div className="hidden md:block text-right pb-2">
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Нийт</div>
              <div
                className="mt-1 font-semibold tabular-nums text-white leading-none"
                style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 36 }}
              >
                {list.length}
              </div>
              <div className="text-[11px] mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>брэнд</div>
            </div>
          </div>
          <p className="mt-5 text-[14px] sm:text-[15px] leading-6 sm:leading-7 max-w-xl" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {vendorName}-ийн албан ёсны түнш брэндүүд — нэг дороос үзээрэй.
          </p>
        </motion.div>
      </div>

      {list.length > 6 && (
        <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pb-10">
          <label className="relative inline-block w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color: 'rgba(255,255,255,0.5)' }} />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Брэнд хайх…"
              className="w-full h-10 pl-9 pr-3 rounded-full text-[13px] outline-none border-0 focus:outline-none focus:ring-0"
              style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)', color: 'white' }}
            />
          </label>
        </div>
      )}

      {spotlight && !query && (
        <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pb-10">
          <motion.a
            href={navigateUrl(`/product?brand=${spotlight.id}`, pathname)}
            className="group block relative rounded-2xl sm:rounded-3xl overflow-hidden aspect-4/3 sm:aspect-21/9 lg:aspect-21/8 cursor-pointer"
            style={{ border: '1px solid rgba(255,255,255,0.13)' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: 'easeOut' }}
            viewport={{ once: true }}
          >
            <img
              src={formatImageURL(spotlight.thumbnail)}
              alt={spotlight.name}
              className="absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out will-change-transform group-hover:scale-105"
            />
            <div className="absolute inset-0 sm:hidden" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.15) 30%, rgba(0,0,0,0.85) 100%)' }} />
            <div className="absolute inset-0 hidden sm:block" style={{ background: 'linear-gradient(110deg, rgba(0,0,0,0.75) 35%, rgba(0,0,0,0.05) 80%)' }} />
            <div className="relative h-full p-5 sm:p-14 flex flex-col justify-end max-w-2xl">
              <div className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>
                <span className="px-2 py-0.5 rounded-full bg-white text-[10px]" style={{ color: '#0a0a0c' }}>SPOTLIGHT</span>
                <span className="hidden xs:inline sm:inline">Онцлох брэнд</span>
              </div>
              <h2
                className="mt-3 sm:mt-4 font-semibold tracking-[-0.025em] leading-none text-white line-clamp-2"
                style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(24px, 5vw, 52px)' }}
              >
                {spotlight.name}
              </h2>
              <div className="mt-4 sm:mt-5">
                <span
                  className="inline-flex items-center gap-1.5 h-9 sm:h-10 px-4 sm:px-5 rounded-full text-[12px] sm:text-[13px] font-medium max-w-full"
                  style={{ background: 'white', color: '#0a0a0c' }}
                >
                  <span className="truncate">{spotlight.name}-н бараанууд</span>
                  <ArrowRight className="h-3.5 w-3.5 shrink-0" />
                </span>
              </div>
            </div>
          </motion.a>
        </div>
      )}

      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pb-20">
        {rest.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {rest.map((b: any, i: number) => <BrandCard key={b.id} brand={b} index={i} />)}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 py-24" style={{ color: 'rgba(255,255,255,0.5)' }}>
            <Tag className="h-8 w-8" />
            <div className="text-[14px]">
              {query ? `"${query}" гэсэн нэртэй брэнд олдсонгүй` : 'Брэнд олдсонгүй'}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
